// HelpAssistant/useConsentObstruction.js
//
// How tall the bottom-docked GDPR consent banner currently is, or 0 when it is
// not on screen. The Help FAB is clamped above this so it can never own the
// pixels of the Accept/Reject buttons (see fabPosition.js for the measured
// evidence that it did).
//
// `useConsent()` is the documented source of truth for whether the banner is
// mounted (ConsentContext renders it exactly when `gaConfigured && !hasChoice`),
// so that boolean is read from the context rather than inferred. The HEIGHT,
// however, is genuinely dynamic — measured live at 100px @1440, 100px @1024,
// 121px @768 and 206px @390 — so it is read off the real element rather than
// hard-coded per breakpoint.
//
// Outside a ConsentProvider the context default has `gaConfigured: false`, so
// this returns 0 and the FAB behaves exactly as before.

import { useEffect, useState } from "react";
import { useConsent } from "../../contexts/ConsentContext";

/** The banner's own root class (see components/ConsentBanner/index.jsx). */
export const CONSENT_BANNER_SELECTOR = ".iyuga-consent-banner";

/** Measured height of the consent banner, 0 when absent. Never throws. */
export const measureConsentBannerHeight = () => {
  try {
    const el = document.querySelector(CONSENT_BANNER_SELECTOR);
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    return rect.height > 0 ? Math.ceil(rect.height) : 0;
  } catch (e) {
    return 0;
  }
};

export default function useConsentObstruction() {
  const { gaConfigured, hasChoice } = useConsent();
  const bannerMounted = Boolean(gaConfigured) && !hasChoice;
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!bannerMounted) {
      setHeight(0);
      return undefined;
    }
    const measure = () =>
      setHeight((prev) => {
        const next = measureConsentBannerHeight();
        return next === prev ? prev : next;
      });
    // Measure now, and once more after paint: the banner slides up on mount, so
    // the first synchronous read can catch it mid-transition.
    measure();
    const raf =
      typeof window.requestAnimationFrame === "function"
        ? window.requestAnimationFrame(measure)
        : null;
    // The banner reflows (100px -> 206px) as the viewport narrows.
    window.addEventListener("resize", measure);
    return () => {
      if (raf && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(raf);
      }
      window.removeEventListener("resize", measure);
    };
  }, [bannerMounted]);

  return height;
}
